import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import Layout from '../components/Layout';

const Maintenance = () => {
  const [requests, setRequests] = useState([]);
  const [workOrders, setWorkOrders] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [activeTab, setActiveTab] = useState('requests');

  useEffect(() => {
    fetchMaintenanceData();
  }, []);

  const fetchMaintenanceData = async () => {
    // Fetch maintenance requests
    const { data: requestData } = await supabase
      .from('maintenance_requests')
      .select(`
        *,
        units(unit_number, properties(name)),
        residents(first_name, last_name)
      `)
      .order('created_at', { ascending: false });
    setRequests(requestData || []);

    // Fetch work orders
    const { data: workOrderData } = await supabase
      .from('work_orders')
      .select(`
        *,
        vendors(name, company),
        maintenance_requests(title)
      `)
      .order('created_at', { ascending: false });
    setWorkOrders(workOrderData || []);

    // Fetch vendors
    const { data: vendorData } = await supabase
      .from('vendors')
      .select('*')
      .order('name');
    setVendors(vendorData || []);
  };

  const updateRequestStatus = async (requestId, status) => {
    await supabase
      .from('maintenance_requests')
      .update({ status })
      .eq('id', requestId);
    fetchMaintenanceData();
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'emergency': return '#dc3545';
      case 'high': return '#fd7e14';
      case 'medium': return '#ffc107';
      case 'low': return '#28a745';
      default: return '#6c757d';
    }
  };

  return (
    <Layout>
      <div style={{ padding: '20px' }}>
        <h2>Maintenance Management</h2>
        
        <div style={{ display: 'flex', gap: '20px', marginBottom: '30px' }}>
          <button 
            onClick={() => setActiveTab('requests')}
            className={`btn ${activeTab === 'requests' ? 'btn-primary' : 'btn-secondary'}`}
          >
            Maintenance Requests
          </button>
          <button 
            onClick={() => setActiveTab('workorders')}
            className={`btn ${activeTab === 'workorders' ? 'btn-primary' : 'btn-secondary'}`}
          >
            Work Orders
          </button>
          <button 
            onClick={() => setActiveTab('vendors')}
            className={`btn ${activeTab === 'vendors' ? 'btn-primary' : 'btn-secondary'}`}
          >
            Vendors
          </button>
        </div>

        {activeTab === 'requests' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3>Maintenance Requests</h3>
              <button className="btn btn-primary">New Request</button>
            </div>
            
            <div style={{ display: 'grid', gap: '15px' }}>
              {requests.map(request => (
                <div key={request.id} style={{ background: 'white', border: '1px solid #ddd', borderRadius: '8px', padding: '20px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '15px' }}>
                    <div>
                      <h4>{request.title}</h4>
                      <p style={{ color: '#666', margin: '5px 0' }}>
                        {request.units?.properties?.name} - Unit {request.units?.unit_number}
                      </p>
                      <p style={{ color: '#666', margin: '5px 0' }}>
                        Resident: {request.residents?.first_name} {request.residents?.last_name}
                      </p>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <span style={{
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '12px',
                        background: getPriorityColor(request.priority),
                        color: 'white'
                      }}>
                        {request.priority}
                      </span>
                      <p style={{ margin: '5px 0', fontSize: '12px', color: '#666' }}>
                        {new Date(request.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  
                  <p>{request.description}</p>
                  
                  <div style={{ display: 'flex', gap: '10px', marginTop: '15px' }}>
                    <select 
                      value={request.status}
                      onChange={(e) => updateRequestStatus(request.id, e.target.value)}
                      style={{ padding: '5px 10px', borderRadius: '4px', border: '1px solid #ddd' }}
                    >
                      <option value="open">Open</option>
                      <option value="in_progress">In Progress</option>
                      <option value="completed">Completed</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                    <button className="btn btn-secondary" style={{ fontSize: '12px' }}>Create Work Order</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'workorders' && (
          <div>
            <h3>Work Orders</h3>
            <div style={{ display: 'grid', gap: '15px' }}>
              {workOrders.map(order => (
                <div key={order.id} style={{ background: 'white', border: '1px solid #ddd', borderRadius: '8px', padding: '20px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <h4>{order.maintenance_requests?.title}</h4>
                      <p style={{ color: '#666' }}>Vendor: {order.vendors?.name} ({order.vendors?.company})</p>
                      <p>Estimated Cost: ${order.estimated_cost}</p>
                    </div>
                    <span style={{
                      padding: '4px 8px',
                      borderRadius: '4px',
                      fontSize: '12px',
                      background: order.status === 'completed' ? '#d4edda' : '#fff3cd',
                      color: order.status === 'completed' ? '#155724' : '#856404'
                    }}>
                      {order.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'vendors' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3>Vendors</h3>
              <button className="btn btn-primary">Add Vendor</button>
            </div>
            
            <div style={{ display: 'grid', gap: '15px' }}>
              {vendors.map(vendor => (
                <div key={vendor.id} style={{ background: 'white', border: '1px solid #ddd', borderRadius: '8px', padding: '20px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <h4>{vendor.name}</h4>
                      <p style={{ color: '#666' }}>{vendor.company}</p>
                      <p>{vendor.email} | {vendor.phone}</p>
                      {vendor.hourly_rate && <p>Rate: ${vendor.hourly_rate}/hour</p>}
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      {vendor.rating && (
                        <p>Rating: {vendor.rating}/5.0</p>
                      )}
                      <button className="btn btn-secondary" style={{ fontSize: '12px' }}>Edit</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Maintenance;